/* -----------------Globals--------------- */
import { useEffect } from "react";

/* -----------------Helpers & Hooks--------------- */
import { type Editor, EditorContent, useEditor } from "@tiptap/react";

import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
/* -----------------Types--------------- */
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import type { FontSchemaType } from "../../../../font";
import { useAppTheme } from "../../../../useAppTheme";
/* -----------------Child components--------------- */
import Box from "../../../Box";
import RenderMarkdown from "../../../MarkdownRenderer";
import { decodeHtmlEntities } from "../../../MarkdownRenderer/decode-html-entities";
import { CustomImageExtension } from "./ImageExtension";

type StemBaseProps = {
	text: string;
	fontSize?: number;
	appFont?: FontSchemaType;
	userSelect?: "none" | "text";
};

type MarkdownStemProps = {
	handleClickInsertableText?: (choiceId: string) => void;
	showTTS?: boolean;
	disableInsertableText?: boolean;
	renderTTS?: () => React.ReactNode;
	paragraphMarginVertical?: number;
};

type TipTapStemProps = {
	handleStoreHtml?: (html: string) => void;
	highlightedHtml?: string;
	setEditorRef: (editor: Editor) => void;
};

type Props = StemBaseProps &
	MarkdownStemProps &
	TipTapStemProps & {
		isSummativeTest?: boolean;
	};

const ItemStemMarkdownRenderer = ({
	text,
	fontSize = 16,
	handleClickInsertableText,
	showTTS,
	disableInsertableText,
	renderTTS,
	appFont,
	paragraphMarginVertical = 6,
	userSelect = "none",
}: StemBaseProps & MarkdownStemProps) => {
	return (
		<Box flexDirection="row" gap={8} width="100%" alignItems="center">
			{showTTS && renderTTS && renderTTS()}
			<Box flex={1} userSelect={userSelect}>
				<RenderMarkdown
					text={text}
					fontSize={fontSize}
					variant="stem"
					appFont={appFont}
					handleClickInsertableText={handleClickInsertableText}
					disableInsertableText={disableInsertableText}
					paragraphMarginVertical={paragraphMarginVertical}
				/>
			</Box>
		</Box>
	);
};

const ItemStemTipTap = ({
	text,
	handleStoreHtml,
	highlightedHtml,
	fontSize = 16,
	appFont,
	setEditorRef,
}: StemBaseProps & TipTapStemProps) => {
	const theme = useAppTheme();
	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			CustomImageExtension,
			Highlight.configure({
				HTMLAttributes: {
					class: "highlight",
				},
			}),
			Table.configure({
				resizable: false,
			}),
			TableRow,
			TableHeader,
			TableCell,
		],
		content: "",
		editable: false,
		onUpdate: ({ editor: updateEditor }) => {
			if (handleStoreHtml) {
				handleStoreHtml(updateEditor.getHTML());
			}
		},
	});

	useEffect(() => {
		if (editor) {
			setEditorRef(editor);
		}
	}, [editor, setEditorRef]);

	useEffect(() => {
		if (editor) {
			editor.commands.setContent(decodeHtmlEntities(highlightedHtml || text));
		}
	}, [editor, highlightedHtml, text]);

	return (
		<>
			<style>
				{`
					/* Base Styles */
					.ProseMirror {
						font-family: "${appFont}-regular" !important;
						font-size: ${fontSize}px !important;
						line-height: ${fontSize * 1.5}px !important;
						color: ${theme.colors.text} !important;
					}
					
					.ProseMirror * {
						color: inherit !important;
					}
					
					.ProseMirror p {
						margin: 0 0 ${fontSize / 14}px 0 !important;
						line-height: ${fontSize * 1.5}px !important;
						padding: 0 !important;
					}
					
					.ProseMirror p + ul,
					.ProseMirror p + ol {
						margin-top: -${(10 * fontSize) / 14}px !important;
					}
					
					.ProseMirror strong {
						font-family: "${appFont}-bold" !important;
						font-weight: normal !important;
					}
					
					.ProseMirror ul,
					.ProseMirror ol {
						padding-left: 20px !important;
						margin: 0 0 ${fontSize * 0.5}px 0 !important;
					}
					
					.ProseMirror ul { list-style-type: disc !important; }
					.ProseMirror ol { list-style-type: decimal !important; }
					
					.ProseMirror li {
						margin-top: ${fontSize * 0.2}px !important;
						margin-bottom: 0 !important;
						padding-left: 0 !important;
					}
					
					.ProseMirror li:first-child {
						margin-top: 0 !important;
					}
					
					.ProseMirror li > p {
						margin: 0 !important;
						display: inline !important;
					}
					
					.ProseMirror ul ul,
					.ProseMirror ol ul {
						list-style-type: circle !important;
						margin-top: ${fontSize * 0.2}px !important;
						padding-left: 20px !important;
					}
					
					.ProseMirror ol ol,
					.ProseMirror ul ol {
						list-style-type: lower-alpha !important;
						margin-top: ${fontSize * 0.2}px !important;
						padding-left: 20px !important;
					}
					
					/* Tables */
					.ProseMirror table {
						border-collapse: collapse;
						table-layout: fixed;
						width: 100%;
						margin: 12px 0;
						border: 1px solid ${theme.colors.outline};
					}
					
					.ProseMirror table td,
					.ProseMirror table th {
						min-width: 1em;
						border: 1px solid ${theme.colors.outline};
						padding: 8px 12px;
						vertical-align: top;
						background: ${theme.colors.surface};
					}
					
					.ProseMirror table th {
						font-family: "${appFont}-bold" !important;
						font-weight: normal;
						background: ${theme.colors.surfaceVariant};
						text-align: center;
					}
					
					.ProseMirror table td {
						text-align: center;
					}
				`}
			</style>
			<EditorContent
				editor={editor}
				aria-label="Item stem"
				aria-labelledby="item-stem"
				title="Item stem"
				style={{
					color: theme.colors.text,
					fontFamily: `${appFont}-regular`,
					fontSize: fontSize,
				}}
			/>
		</>
	);
};

const ItemStem = ({
	text,
	handleStoreHtml,
	highlightedHtml,
	fontSize,
	isSummativeTest,
	handleClickInsertableText,
	disableInsertableText,
	showTTS,
	appFont,
	setEditorRef,
	renderTTS,
	paragraphMarginVertical,
	userSelect = "none",
}: Props) => {
	//TODO: Need to add highlighting support for markdown renderer, then we can remove tiptap
	if (!isSummativeTest) {
		return (
			<ItemStemMarkdownRenderer
				text={text}
				fontSize={fontSize}
				handleClickInsertableText={handleClickInsertableText}
				disableInsertableText={disableInsertableText}
				showTTS={showTTS}
				appFont={appFont}
				renderTTS={renderTTS}
				paragraphMarginVertical={paragraphMarginVertical}
				userSelect={userSelect}
			/>
		);
	}

	return (
		<ItemStemTipTap
			text={text}
			handleStoreHtml={handleStoreHtml}
			highlightedHtml={highlightedHtml}
			fontSize={fontSize}
			appFont={appFont}
			setEditorRef={setEditorRef}
		/>
	);
};

export default ItemStem;
